import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, UserPlus, UserMinus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Software, StatusLicenca } from '@/types/software';
import { 
  useSoftwareLicencas, 
  useCreateMultipleLicencas, 
  useLiberarLicenca,
  useDeleteLicenca 
} from '@/hooks/useSoftwareLicencas';
import { AtribuirLicencaDialog } from './AtribuirLicencaDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const statusConfig: Record<StatusLicenca, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  disponivel: { label: 'Disponível', variant: 'secondary' },
  em_uso: { label: 'Em Uso', variant: 'default' },
  bloqueada: { label: 'Bloqueada', variant: 'outline' },
  expirada: { label: 'Expirada', variant: 'destructive' },
};

interface SoftwareLicencasTabProps {
  software: Software;
}

export function SoftwareLicencasTab({ software }: SoftwareLicencasTabProps) {
  const { data: licencas, isLoading } = useSoftwareLicencas(software.id);
  const createMultipleLicencas = useCreateMultipleLicencas();
  const liberarLicenca = useLiberarLicenca();
  const deleteLicenca = useDeleteLicenca();

  const [quantidadeNovas, setQuantidadeNovas] = useState(1);
  const [isAddingLicencas, setIsAddingLicencas] = useState(false);
  const [atribuirLicencaId, setAtribuirLicencaId] = useState<string | null>(null);
  const [deletingLicencaId, setDeletingLicencaId] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleAddLicencas = async () => {
    await createMultipleLicencas.mutateAsync({
      softwareId: software.id,
      quantidade: quantidadeNovas,
    });
    setIsAddingLicencas(false);
    setQuantidadeNovas(1);
  };

  const handleLiberar = async (licencaId: string) => {
    await liberarLicenca.mutateAsync(licencaId);
  };

  const handleDelete = async () => {
    if (deletingLicencaId) {
      await deleteLicenca.mutateAsync(deletingLicencaId);
      setDeletingLicencaId(null);
      
      // Adjust current page if needed after deletion
      const newTotalItems = totalItems - 1;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }
  };

  const licencasDisponiveis = licencas?.filter(l => l.status === 'disponivel').length || 0;
  const licencasEmUso = licencas?.filter(l => l.status === 'em_uso').length || 0;

  // Pagination calculations
  const totalItems = licencas?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLicencas = licencas?.slice(startIndex, endIndex) || [];

  // Reset to first page when licenses change
  const handleAddLicencasWithReset = async () => {
    await handleAddLicencas();
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="space-y-4">
      {/* Resumo */}
      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
        <div className="flex gap-6">
          <div>
            <span className="text-sm text-muted-foreground">Contratadas:</span>
            <span className="ml-2 font-semibold">{software.quantidade_contratada}</span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Cadastradas:</span>
            <span className="ml-2 font-semibold">{licencas?.length || 0}</span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Em Uso:</span>
            <span className="ml-2 font-semibold text-primary">{licencasEmUso}</span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Disponíveis:</span>
            <span className="ml-2 font-semibold text-green-600">{licencasDisponiveis}</span>
          </div>
        </div>

        {!isAddingLicencas ? (
          <Button type="button" onClick={() => setIsAddingLicencas(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Licenças
          </Button>
        ) : (
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="quantidade">Quantidade</Label>
              <Input
                id="quantidade"
                type="number"
                min={1}
                value={quantidadeNovas}
                onChange={(e) => setQuantidadeNovas(parseInt(e.target.value) || 1)}
                className="w-20"
              />
            </div>
            <Button type="button" onClick={handleAddLicencasWithReset} disabled={createMultipleLicencas.isPending} size="sm">
              {createMultipleLicencas.isPending ? 'Criando...' : 'Criar'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setIsAddingLicencas(false)} size="sm">
              Cancelar
            </Button>
          </div>
        )}
      </div>

      {/* Tabela de Licenças */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Carregando licenças...</div>
      ) : !licencas?.length ? (
        <div className="text-center py-8 text-muted-foreground">
          Nenhuma licença cadastrada. Clique em "Adicionar Licenças" para criar.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Chave</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Ativo/Máquina</TableHead>
                <TableHead>Data Atribuição</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentLicencas.map((licenca, index) => (
                <TableRow key={licenca.id}>
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {licenca.chave_licenca || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusConfig[licenca.status].variant}>
                      {statusConfig[licenca.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {licenca.usuario ? (
                      <div>
                        <div className="font-medium">{licenca.usuario.name}</div>
                        <div className="text-xs text-muted-foreground">{licenca.usuario.email}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{licenca.ativo?.nome || '-'}</TableCell>
                  <TableCell>
                    {licenca.data_atribuicao 
                      ? new Date(licenca.data_atribuicao).toLocaleDateString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {licenca.status === 'disponivel' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setAtribuirLicencaId(licenca.id)}
                          title="Atribuir a usuário"
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      )}
                      {licenca.status === 'em_uso' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleLiberar(licenca.id)}
                          disabled={liberarLicenca.isPending}
                          title="Liberar licença"
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      )}
                      {licenca.status === 'disponivel' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingLicencaId(licenca.id)}
                          title="Excluir licença"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} licenças
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show first page, last page, current page, and pages around current
                  return page === 1 || 
                         page === totalPages || 
                         Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  // Add ellipsis if there's a gap
                  const prevPage = array[index - 1];
                  const showEllipsis = prevPage && page - prevPage > 1;
                  
                  return (
                    <div key={page} className="flex items-center">
                      {showEllipsis && (
                        <span className="px-2 text-muted-foreground">...</span>
                      )}
                      <Button
                        type="button"
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    </div>
                  );
                })}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog de Atribuição */}
      {atribuirLicencaId && (
        <AtribuirLicencaDialog
          licencaId={atribuirLicencaId}
          open={!!atribuirLicencaId}
          onOpenChange={(open) => !open && setAtribuirLicencaId(null)}
        />
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deletingLicencaId} onOpenChange={(open) => !open && setDeletingLicencaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta licença? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
